import React from 'react'
import { useAssessmentContext } from 'contexts/AssessmentContext'
import { flattenQuestionnaireItems } from 'components/Patients/Assessments/CreateAssessment/helpers'
import PatientVerificationSubsection from './subsections/patient-verification/PatientVerificationSubsection'
import HistoryAndDiagnosesSubsection from './subsections/history-and-diagnoses/HistoryAndDiagnosesSubsection'
import { ASSESSMENTS_STATIC_DATA } from '../../constants'
import { PATSectionComponentProps } from '../../types/components'
import { normalizeTherapyCode } from '../../helpers'

function PatientBackgroundSection({
  item: section,
  sectionId: _sectionId,
  sectionIndex: _sectionIndex
}: PATSectionComponentProps): JSX.Element {
  const { cantEditAssessmentFields, assessmentMustBeFinalized } = useAssessmentContext()

  const historyAndDiagnosesSubsection = section?.item?.find(
    (sub) => sub.linkId?.split('/')[1] === 'history-and-diagnoses'
  )

  // Flatten nested fields for history and diagnoses (questions are nested under groups like 'er', 'hospital', etc.)
  const historyAndDiagnosesFields = React.useMemo(() => {
    if (!historyAndDiagnosesSubsection?.item) {
      return {}
    }
    return flattenQuestionnaireItems(historyAndDiagnosesSubsection.item)
  }, [historyAndDiagnosesSubsection])

  // Get therapy data from static data
  const therapyData = ASSESSMENTS_STATIC_DATA.pat.therapyTypes

  const therapyOptions = React.useMemo(() => {
    if (!therapyData) return []
    return therapyData.map((item) => {
      const display = item.valueCoding.display || ''
      const shortCode = item.valueCoding.code || ''
      // Use helper function to normalize therapy code by label (for UI component use)
      const normalizedCode = normalizeTherapyCode(display, 'label') || shortCode.toLowerCase()

      return {
        label: display,
        value: normalizedCode, // Use normalized code as value for Select
        code: normalizedCode,
        display: display
      }
    })
  }, [therapyData])

  // Guard clause: return early if section is undefined, null, or has no items
  if (!section || !section.item || !Array.isArray(section.item)) {
    return <></>
  }

  // Create field mapping from questionnaire items
  const fieldMap = flattenQuestionnaireItems(section.item || [])

  // Find subsections
  const patientVerificationSubsection = section.item.find(
    (sub) => sub.linkId?.split('/')[1] === 'patient-verification'
  )

  return (
    <>
      {patientVerificationSubsection && (
        <PatientVerificationSubsection
          subsection={patientVerificationSubsection}
          fieldMap={fieldMap}
        />
      )}

      {historyAndDiagnosesSubsection && (
        <HistoryAndDiagnosesSubsection
          subsection={historyAndDiagnosesSubsection}
          fields={historyAndDiagnosesFields}
          cantEditAssessmentFields={cantEditAssessmentFields}
          assessmentMustBeFinalized={assessmentMustBeFinalized}
          therapyOptions={therapyOptions}
        />
      )}
    </>
  )
}

export default PatientBackgroundSection
